import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Hash, FileText, Settings2, Link2, ArrowRight, X, Sparkles, Type } from 'lucide-react';
import MethodBadge from '@/components/layout/MethodBadge';
import { useOpenApi } from '@/hooks/useOpenApi';
import { listEndpoints } from '@/lib/openapi/parse';
import { fuzzyFilter } from '@/lib/fuzzy';
import { showToast } from '@/lib/toast';
import type { Method } from '@/lib/tokens';
import SemanticTab from './SemanticTab';
import { useSemanticSearch } from '@/hooks/useSemanticSearch';

type PaletteMode = 'fuzzy' | 'semantic';

type Group = 'endpoint' | 'page' | 'action';

interface BaseItem {
    id: string;
    group: Group;
    label: string;
    sublabel?: string;
}
interface EndpointItem extends BaseItem {
    group: 'endpoint';
    method: Method;
    path: string;
    operationId: string;
}
interface PageItem extends BaseItem {
    group: 'page';
    to: string;
}
interface ActionItem extends BaseItem {
    group: 'action';
    run: () => void;
}
type Item = EndpointItem | PageItem | ActionItem;

const PAGES: PageItem[] = [
    { id: 'page:getting-started', group: 'page', label: 'Getting Started', sublabel: '/', to: '/' },
    { id: 'page:scenarios', group: 'page', label: 'Scenarios', sublabel: '/scenarios', to: '/scenarios' },
];

const MAX_RESULTS = 12;

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function CommandPalette({ open, onClose }: Props) {
    const navigate = useNavigate();
    const { spec } = useOpenApi();
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const [mode, setMode] = useState<PaletteMode>('fuzzy');
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Reset state every time we open
    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIdx(0);
            setMode('fuzzy');
            // Focus input
            queueMicrotask(() => inputRef.current?.focus());
        }
    }, [open]);

    // Reset active idx kalau mode berganti
    useEffect(() => {
        setActiveIdx(0);
    }, [mode]);

    const actions: ActionItem[] = useMemo(
        () => [
            {
                id: 'action:mcp-config',
                group: 'action',
                label: 'Open MCP config',
                sublabel: 'Tampilkan setup MCP server',
                run: () => {
                    window.dispatchEvent(new CustomEvent('palette:open-mcp'));
                    showToast('MCP config dibuka');
                },
            },
            {
                id: 'action:copy-url',
                group: 'action',
                label: 'Copy current URL',
                sublabel: 'Salin URL halaman ini',
                run: () => {
                    try {
                        const url = window.location.href;
                        if (navigator.clipboard?.writeText) {
                            navigator.clipboard.writeText(url);
                        }
                        showToast('URL disalin');
                    } catch {
                        showToast('Gagal menyalin URL');
                    }
                },
            },
        ],
        [],
    );

    const allEndpoints = useMemo<EndpointItem[]>(() => {
        if (!spec) return [];
        return listEndpoints(spec).map((e) => ({
            id: `endpoint:${e.operationId}`,
            group: 'endpoint' as const,
            label: e.summary,
            sublabel: `${e.method.toUpperCase()} ${e.path}`,
            method: e.method.toUpperCase() as Method,
            path: e.path,
            operationId: e.operationId,
        }));
    }, [spec]);

    const filtered = useMemo(() => {
        const q = query.trim();
        // For each group, filter & take top results.
        const endpoints = fuzzyFilter(q, allEndpoints, (it) => `${it.label} ${it.path}`)
            .slice(0, MAX_RESULTS)
            .map((m) => m.item);
        const pages = fuzzyFilter(q, PAGES, (it) => `${it.label} ${it.sublabel || ''}`).map((m) => m.item);
        const acts = fuzzyFilter(q, actions, (it) => `${it.label} ${it.sublabel || ''}`).map((m) => m.item);

        // Build a flat list for keyboard navigation while preserving group order
        const flat: Item[] = [...endpoints, ...pages, ...acts].slice(0, MAX_RESULTS);
        return { endpoints, pages, actions: acts, flat };
    }, [query, allEndpoints, actions]);

    // Semantic results (only active ketika mode === 'semantic')
    const semantic = useSemanticSearch(mode === 'semantic' ? query : '');

    const totalAvailable = allEndpoints.length + PAGES.length + actions.length;

    // Compute nav length berdasarkan mode aktif
    const navLength = mode === 'semantic' ? semantic.results.length : filtered.flat.length;

    // Clamp activeIdx whenever nav length changes
    useEffect(() => {
        if (activeIdx >= navLength) {
            setActiveIdx(0);
        }
    }, [navLength, activeIdx]);

    // Body scroll lock + focus restoration
    useEffect(() => {
        if (!open) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    if (!open) return null;

    function runItem(item: Item) {
        if (item.group === 'endpoint') {
            navigate('/r/' + item.operationId);
        } else if (item.group === 'page') {
            navigate(item.to);
        } else {
            item.run();
        }
        onClose();
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => (navLength === 0 ? 0 : (i + 1) % navLength));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => (navLength === 0 ? 0 : (i - 1 + navLength) % navLength));
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (mode === 'semantic') {
                const hit = semantic.results[activeIdx];
                if (hit) {
                    navigate('/r/' + hit.operationId);
                    onClose();
                }
                return;
            }
            const item = filtered.flat[activeIdx];
            if (item) runItem(item);
        }
        // Toggle mode via Cmd/Ctrl+/
        if (e.key === '/' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            setMode((m) => (m === 'fuzzy' ? 'semantic' : 'fuzzy'));
        }
    }

    function onBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
        if (e.target === e.currentTarget) onClose();
    }

    // Compute the absolute index for each item so we can highlight + click
    const flatIds = filtered.flat.map((i) => i.id);

    return (
        <div
            data-testid="palette-backdrop"
            onClick={onBackdropClick}
            onKeyDown={onKeyDown}
            className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4 bg-black/60 backdrop-blur"
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
        >
            <div
                className="w-full max-w-[640px] rounded-xl overflow-hidden shadow-2xl flex flex-col"
                style={{
                    backgroundColor: 'var(--surface-1)',
                    border: '1px solid var(--border)',
                    maxHeight: 'min(480px, 80vh)',
                }}
            >
                {/* Header */}
                <div className="flex items-center gap-2 px-4 h-12 border-b" style={{ borderColor: 'var(--border)' }}>
                    <Search className="h-4 w-4 text-muted shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Cari endpoint, halaman, atau aksi…"
                        autoFocus
                        aria-label="Search"
                        className="flex-1 bg-transparent text-sm text-ink placeholder:text-ghost focus:outline-none"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Tutup"
                        className="hidden sm:flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded text-muted hover:text-ink"
                        style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-strong)' }}
                    >
                        ESC
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Tutup palette"
                        className="sm:hidden p-1 text-muted hover:text-ink"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Mode tabs */}
                <div
                    className="flex items-center gap-1 px-3 py-1.5 border-b text-[11px]"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg)' }}
                >
                    <ModeTab
                        active={mode === 'fuzzy'}
                        onClick={() => setMode('fuzzy')}
                        icon={<Type className="h-3 w-3" />}
                        label="Cocok kata"
                    />
                    <ModeTab
                        active={mode === 'semantic'}
                        onClick={() => setMode('semantic')}
                        icon={<Sparkles className="h-3 w-3" />}
                        label="Cari makna"
                    />
                    <span className="ml-auto text-[10px] text-ghost hidden sm:inline">
                        {mode === 'semantic' ? 'AI semantic via Workers AI' : 'Fuzzy match lokal'}
                    </span>
                </div>

                {/* Body */}
                <div ref={listRef} className="flex-1 overflow-y-auto py-1.5" data-testid="palette-list">
                    {mode === 'semantic' ? (
                        <SemanticTab
                            query={query}
                            results={semantic.results}
                            busy={semantic.busy}
                            error={semantic.error}
                            activeIdx={activeIdx}
                            onActiveChange={setActiveIdx}
                            onSelect={(opId) => {
                                navigate('/r/' + opId);
                                onClose();
                            }}
                        />
                    ) : filtered.flat.length === 0 ? (
                        <div className="px-4 py-10 text-center text-sm text-muted">
                            Tidak ada hasil untuk "{query}"
                        </div>
                    ) : (
                        <>
                            {filtered.endpoints.length > 0 && (
                                <Section title="Endpoints">
                                    {filtered.endpoints.map((it) => {
                                        const idx = flatIds.indexOf(it.id);
                                        return (
                                            <Row
                                                key={it.id}
                                                active={idx === activeIdx}
                                                onMouseEnter={() => setActiveIdx(idx)}
                                                onClick={() => runItem(it)}
                                                testId={`palette-item-${it.id}`}
                                            >
                                                <MethodBadge method={it.method} size="xs" />
                                                <span className="truncate text-ink text-sm">{it.label}</span>
                                                <span className="truncate text-xs text-muted font-mono ml-auto">{it.path}</span>
                                            </Row>
                                        );
                                    })}
                                </Section>
                            )}

                            {filtered.pages.length > 0 && (
                                <Section title="Halaman">
                                    {filtered.pages.map((it) => {
                                        const idx = flatIds.indexOf(it.id);
                                        return (
                                            <Row
                                                key={it.id}
                                                active={idx === activeIdx}
                                                onMouseEnter={() => setActiveIdx(idx)}
                                                onClick={() => runItem(it)}
                                                testId={`palette-item-${it.id}`}
                                            >
                                                <FileText className="h-3.5 w-3.5 text-muted shrink-0" />
                                                <span className="text-ink text-sm">{it.label}</span>
                                                <span className="text-xs text-muted ml-auto font-mono">{it.sublabel}</span>
                                            </Row>
                                        );
                                    })}
                                </Section>
                            )}

                            {filtered.actions.length > 0 && (
                                <Section title="Aksi">
                                    {filtered.actions.map((it) => {
                                        const idx = flatIds.indexOf(it.id);
                                        const Icon = it.id.includes('mcp') ? Settings2 : it.id.includes('copy') ? Link2 : Hash;
                                        return (
                                            <Row
                                                key={it.id}
                                                active={idx === activeIdx}
                                                onMouseEnter={() => setActiveIdx(idx)}
                                                onClick={() => runItem(it)}
                                                testId={`palette-item-${it.id}`}
                                            >
                                                <Icon className="h-3.5 w-3.5 text-muted shrink-0" />
                                                <span className="text-ink text-sm">{it.label}</span>
                                                {it.sublabel && <span className="text-xs text-muted ml-auto truncate">{it.sublabel}</span>}
                                            </Row>
                                        );
                                    })}
                                </Section>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-between px-4 h-9 border-t text-[11px] text-muted"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg)' }}
                >
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Enter</span>
                        <span className="hidden sm:flex items-center gap-1">↑ ↓ Navigate</span>
                    </div>
                    <div data-testid="palette-count">
                        {mode === 'semantic'
                            ? `${semantic.results.length} hasil semantic`
                            : `${filtered.flat.length} dari ${totalAvailable}`}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ModeTab({
    active,
    onClick,
    icon,
    label,
}: {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`palette-mode-${label.toLowerCase().replace(/\s+/g, '-')}`}
            data-active={active ? 'true' : 'false'}
            className="flex items-center gap-1.5 px-2 py-1 rounded transition-colors"
            style={{
                color: active ? 'var(--accent)' : 'var(--ghost)',
                backgroundColor: active ? 'var(--surface-hover)' : 'transparent',
                border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`,
            }}
        >
            {icon}
            <span className="font-medium">{label}</span>
        </button>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="py-1">
            <div className="px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider text-ghost font-semibold">{title}</div>
            <div>{children}</div>
        </div>
    );
}

function Row({
    active,
    onClick,
    onMouseEnter,
    children,
    testId,
}: {
    active: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    children: React.ReactNode;
    testId?: string;
}) {
    return (
        <div
            role="option"
            aria-selected={active}
            data-testid={testId}
            data-active={active ? 'true' : 'false'}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            className="flex items-center gap-2.5 px-4 py-2 cursor-pointer transition-colors"
            style={{
                backgroundColor: active ? 'var(--surface-hover)' : 'transparent',
                borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
                paddingLeft: active ? '14px' : '16px',
            }}
        >
            {children}
        </div>
    );
}
